import React from 'react';

import { ApIcon, ApSimpleDropdown, colors } from '@alixpartners/ui-components';
import { ColumnChart } from './ColumnChart';
import { Legend } from './Legend';
import { CHART_COLORS } from './utils';
import {
	getBudgetDimensions,
	getBudgetVariance,
	getBudgetDataExport,
} from 'utils/apiRequests';
import {
	Section,
	Title,
	ChartWrapper,
	ArrowRight,
	ArrowWrapper,
	MainChartWrapper,
	SubHeaderWrapper,
	Helper,
	SubTitleWrapper,
} from './BudgetOverview.styles';
import isEqual from 'lodash/isEqual';
import { withRouter } from 'react-router-dom';
import { LOCAL_STORAGE_KEY } from '../utils';
import { CONFIG } from '../routes';
import { setLocalStorageItem } from 'utils/localStorage';

class ThirdChartSection extends React.Component {
	state = {
		dimensions: [],
		selectedDimension: null,
		data: [],
		isLoading: false,
	};

	componentDidMount() {
		this.fetchDimensions();
	}

	componentDidUpdate(prevProps, prevState) {
		const { selectedDimension } = this.state;
		const { selectedIndex, searchItem } = this.props;

		if (
			(selectedDimension &&
				selectedDimension !== prevState.selectedDimension) ||
			selectedIndex !== prevProps.selectedIndex ||
			!isEqual(searchItem, prevProps.searchItem)
		) {
			this.fetchData();
		}
	}

	changeDimension = selectedDimension => {
		this.setState({ selectedDimension });
	};

	getApiParams = () => {
		const { selectedDimension } = this.state;

		if (!selectedDimension) {
			return {};
		}

		const { selectedIndex, searchItem } = this.props;

		return {
			dimension: selectedDimension.value,
			monthNumber: selectedIndex != null ? selectedIndex + 1 : selectedIndex,
			filter: searchItem && searchItem.name,
		};
	};

	fetchData = () => {
		const { selectedDimension } = this.state;

		if (!selectedDimension) {
			return;
		}

		this.setState({ isLoading: true });
		getBudgetVariance(this.getApiParams()).then(data => {
			this.setState({ data, isLoading: false });
		});
	};

	fetchDimensions = () => {
		return getBudgetDimensions().then(dimensions => {
			const options = dimensions
				? dimensions.map(({ description, name }) => {
						return {
							label: description,
							value: name,
						};
				  })
				: [];

			this.setState({ dimensions: options, selectedDimension: options[0] });
		});
	};

	getTotalSeriesData = () => {
		const { total } = this.props;

		return [
			{
				data: [
					{
						color: total < 0 ? CHART_COLORS.RED : CHART_COLORS.GREEN,
						y: total,
					},
				],
			},
		];
	};

	getTotalCategories = () => {
		const { selectedIndex } = this.props;

		return [
			`${
				selectedIndex != null ? `P${selectedIndex + 1}` : 'YTD'
			} incremental savings`,
		];
	};

	getCategories = () => {
		const { data } = this.state;

		return data.map(item => item.dimensionValue);
	};

	getMainSeriesData = () => {
		const { data } = this.state;
		const seriesData = data.map(({ variance }) => ({
			y: variance,
			color: variance < 0 ? CHART_COLORS.RED : CHART_COLORS.GREEN,
		}));

		return [{ name: 'Savings', data: seriesData }];
	};

	handleExport = option => {
		if (option.value === 'xlsx') {
			this.handleDataExport();
		}
	};

	handleDataExport = () => {
		const { onDownloadStart, onDownloadSuccess, onDownloadError } = this.props;

		onDownloadStart();
		getBudgetDataExport(this.getApiParams())
			.catch(onDownloadError)
			.finally(onDownloadSuccess);
	};

	goToPerformance = filterValue => {
		const { selectedDimension } = this.state;
		const { history } = this.props;

		if (!selectedDimension) {
			return;
		}

		const { selectedIndex, searchItem } = this.props;

		const filterModel = {
			filters: { [selectedDimension.value]: [filterValue] },
			monthNumbers: selectedIndex != null ? [String(selectedIndex + 1)] : null,
			primaryFilter: searchItem && searchItem.name,
		};

		setLocalStorageItem(LOCAL_STORAGE_KEY, filterModel);
		history.push(CONFIG[2].path);
	};

	render() {
		const { axisTitle, spendFormatter, total } = this.props;
		const { dimensions, selectedDimension, isLoading } = this.state;
		const isBudgetOverrun = total < 0;

		return (
			<Section>
				<Title>
					Revised Budget vs. Actuals: Incremental Savings
					<ApSimpleDropdown
						iconName="outline_save_alt"
						onChange={this.handleExport}
						options={[
							{
								label: 'Export as data',
								value: 'xlsx',
							},
						]}
						label="Export"
					/>
				</Title>
				<SubHeaderWrapper>
					<SubTitleWrapper>
						<ApIcon iconName="bar_chart" iconColor={colors.grey4} />
						<Helper>Break down by:</Helper>
						<ApSimpleDropdown
							options={dimensions}
							selected={selectedDimension}
							onChange={this.changeDimension}
						/>
					</SubTitleWrapper>
					<Legend />
				</SubHeaderWrapper>
				<ChartWrapper>
					<ColumnChart
						categories={this.getTotalCategories()}
						series={this.getTotalSeriesData()}
						spendFormatter={spendFormatter}
						title={axisTitle}
						width={275}
					/>
					<ArrowWrapper>
						<ArrowRight
							color={isBudgetOverrun ? CHART_COLORS.RED : CHART_COLORS.GREEN}
						/>
					</ArrowWrapper>
					<MainChartWrapper>
						<ColumnChart
							categories={this.getCategories()}
							series={this.getMainSeriesData()}
							spendFormatter={spendFormatter}
							title={axisTitle}
							showTooltip
							isScrollable
							isLoading={isLoading}
							onRedirect={!selectedDimension ? undefined : this.goToPerformance}
						/>
					</MainChartWrapper>
				</ChartWrapper>
			</Section>
		);
	}
}

export default withRouter(ThirdChartSection);
